/* eslint-disable react-hooks/rules-of-hooks */
import { HTTP_STATUS } from "@api/constant";
import MainBlock from "@container/MainBlock";
import MainContainer, { AdsSettings } from "@container/MainContainer";
import SideBlock from "@container/SideBlock/SideBlock";
import WrapperBlock from "@container/WrapperBlock";
import { GADataProps, getGTMData } from "@helper/getGtmData";
import useGlobalAdSetting from "@hooks/useGlobalAdSetting";
import { Route } from "@route-types/PhotoGallery/+types/PhotoGalleryDetailPage";
import ErrorPage from "@routes/ErrorPage";
import NotFound from "@routes/NotFound";
import PhotoGalleryContentCard from "@routes/PhotoGallery/PhotoGalleryContentCard";
import {
  fetchContext,
  ResponseType,
  TRouteWithoutRedirect,
  TRouteWithRedirect,
} from "@sphtech/web2-core/ssr";
import { createMetaTags, MetaTagsProps } from "@sphtech/web2-seo-schema";
import { PageAdTargetingTypeEnum } from "@typings/Ads.d";
import { PhotoGalleryDetailsPayloadResponseType } from "@typings/PagePayloadType";
import { ClientLoaderFunctionArgs, useLoaderData } from "react-router";

import { fetchPhotoGalleryDetailData } from "./PhotoGallery.server";

export function meta(args: Route.MetaArgs) {
  const dataLoaderResponse = args.loaderData as TRouteWithRedirect<
    PhotoGalleryDetailsPayloadResponseType,
    string
  >;
  if (dataLoaderResponse.type === ResponseType.SUCCESS) {
    const metaData = dataLoaderResponse.payload.metaTags as MetaTagsProps;
    return createMetaTags(metaData);
  }
}

export default function PhotoGalleryDetailPage() {
  const dataLoaderResponse: TRouteWithoutRedirect<
    PhotoGalleryDetailsPayloadResponseType,
    string
  > = useLoaderData();
  if (dataLoaderResponse.type === ResponseType.CLIENT_ERROR) {
    return <NotFound />;
  } else if (dataLoaderResponse.type === ResponseType.SERVER_ERROR) {
    return <ErrorPage message="Something went wrong" />;
  }
  const adSettings: AdsSettings = useGlobalAdSetting();
  const pageAdTargetValue = PageAdTargetingTypeEnum.ARTICLE;
  const mainArticle = dataLoaderResponse.payload.article;
  const title = mainArticle.title;
  const sectionName = mainArticle.sectionName ?? "Photo Gallery";

  return (
    <MainContainer
      sectionName={sectionName}
      pageAdTargetValue={pageAdTargetValue}
      suppliedAdsSettings={adSettings}
    >
      <WrapperBlock className="flex flex-col lg:flex-row justify-between">
        <MainBlock sectionName={sectionName} pageName={title} showTitle={false}>
          <PhotoGalleryContentCard
            mainArticle={mainArticle}
            pageAdTargetValue={pageAdTargetValue}
            pageNumber={0}
          />
        </MainBlock>

        <SideBlock
          sectionName={sectionName}
          pageAdTargetValue={pageAdTargetValue}
        />
      </WrapperBlock>
    </MainContainer>
  );
}

export async function loader({ params }: Route.LoaderArgs) {
  const slug = params.slug || "";
  const data = (await fetchPhotoGalleryDetailData(slug)) as TRouteWithRedirect<
    PhotoGalleryDetailsPayloadResponseType,
    string
  >;

  if (data.statusCode !== HTTP_STATUS.SUCCESS) return { ...data };

  const gaData = data.payload.gaData;

  return {
    ...data,
    gtm: getGTMData(gaData as GADataProps),
  };
}

export async function clientLoader(args: ClientLoaderFunctionArgs) {
  const result = (await fetchContext(args)) as { context?: unknown };

  if (!result.context) {
    console.error(
      "Error occured during client-side routing, returning server Loader Data",
    );
    return await args.serverLoader();
  }

  const typedResult: unknown = result.context;

  // Check for valid payload field
  if (
    !typedResult ||
    typeof typedResult !== "object" ||
    !("payload" in typedResult) ||
    !Array.isArray(typedResult.payload)
  ) {
    console.error("Client Loader: fetchContext() returned an invalid payload");
  }
  return typedResult as Promise<
    TRouteWithRedirect<PhotoGalleryDetailsPayloadResponseType, string>
  >;
}
